import React, { useContext, useEffect, useState } from 'react';
import { matchPath, Prompt, Route, Switch, withRouter, Redirect } from 'react-router-dom';

import { PaymentGatewayContext } from 'src/context/payment-gateway-context';
import { SalesforceContext, Steps } from 'src/context/salesforce-context';
import ProgressStepsBar from 'src/components/progress-steps-bar/progress-steps-bar';
import NavigationFooter from 'src/components/navigation-footer/navigation-footer';
import TealiumEvents from 'src/helpers/tealium-events';
import FullPageLoader from 'src/components/full-page-loader/full-page-loader';
import useStateLocalPersist from 'src/hooks/use-state-local-persist';
import { SovModal } from 'src/containers/onboarding/create-account/sov-modal/sov-modal';
import useReducer from 'src/hooks/useReducer';
import { WizardModel } from 'src/containers/onboarding/state/wizard/models/wizard-model';
import { WizardMessageNames } from 'src/containers/onboarding/state/wizard/wizard-message-names';
import { wizardReducer } from 'src/containers/onboarding/state/wizard/reducers/wizard-reducer';
import { NavigateToNextStepMessage } from 'src/containers/onboarding/state/wizard/messages/navigate-to-next-step-message';
import { NavigateToPreviousStepMessage } from 'src/containers/onboarding/state/wizard/messages/navigate-to-previous-step-message';
import { SubmitApplicationModal } from '../submit-application-modal/submit-application-modal';
import FormFooter from '../form-footer/form-footer';
import { isSpecificDeviceSelected } from 'src/helpers/devices';
import { HelpFloatingActionButtonOnboarding, TapOnPhoneModal } from 'src/components';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { publicSiteUrl } from 'src/common';
import './wizard.scss';

function Wizard({ steps: unfilteredSteps, ...props }) {
  const steps = unfilteredSteps.filter((step) => !step.hide || !step.hide());

  const { reserveFunds } = useContext(PaymentGatewayContext);
  const { dispatch, models, registerReducer } = useReducer();
  const { wizard } = models as { wizard: WizardModel };
  const salesforceContext = useContext(SalesforceContext);

  const getStepIndexKey = (stepName, subStepName) =>
    stepName && subStepName ? `${stepName}|${subStepName}` : stepName;

  const stepIndexNameMap = steps.reduce((accum, step, index) => {
    // @ts-ignore
    const stepKey = getStepIndexKey(step.stepName);
    const stepAndSubStepkey = getStepIndexKey(step.stepName, step.subStepName);
    if (!accum[stepAndSubStepkey]) {
      accum[stepAndSubStepkey] = index;
    }

    if (!accum[stepKey]) {
      accum[stepKey] = index;
    }

    return accum;
  }, {});

  const getStepIndex = (stepName, subStepName) =>
    stepIndexNameMap[getStepIndexKey(stepName, subStepName)];

  const [nextEnabled, setNextEnabled] = useState(true);
  const [screenUrlQuickNavigateBack, setScreenUrlQuickNavigateBack] = useStateLocalPersist(
    'is_quick_navigate',
    null,
  );
  const [isQuickNavigateToSingleStep, setIsQuickNavigateToSingleStep] = useStateLocalPersist(
    'is_quick_navigate_to_single_step',
    null,
  );
  const [showSubmitModal, setShowSubmitModalWithoutTagging] = useState(false);
  const [showSovInfoModal, setShowSovInfoModal] = useState(false);
  const [showTapOnPhoneModal, setShowTapOnPhoneModal] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(true);

  const [showBasket, setShowBasket] = useState(false);
  const [showBasketIcon, setShowBasketIcon] = useState(false);
  const {
    hasBuyOnlineDevice,
    paymentGatewayAmount,
    merchant,
    isLoadingFromSessionStorage,
    customerType,
    lead,
    isIncompleteSOVJourney,
  } = useContext(SalesforceContext);

  const setIsLoading = (isLoading) =>
    dispatch(isLoading ? WizardMessageNames.SHOW_PAGE_LOADER : WizardMessageNames.HIDE_PAGE_LOADER);
  const handleNavigateNext = () => dispatch(WizardMessageNames.NAVIGATE_TO_NEXT_STEP);
  const handleNavigateBack = () => dispatch(WizardMessageNames.NAVIGATE_TO_PREVIOUS_STEP);

  const getTotalDevices = (merchant) =>
    Object.values(merchant?.opportunity?.deliveries || {}) // @ts-ignore
      .map((delivery) => delivery.number_of_devices)
      .reduce((a, b) => a + b, 0);

  const isOnlyTapOnPhoneSelected = () => {
    const products = Object.values(merchant.opportunity.deliveries).filter(
      // @ts-ignore
      (product) => product.number_of_devices > 0,
    );

    return (
      products.length === 1 && // @ts-ignore
      products.find((product) => product.device_name === DEVICE_NAMES.TAP_ON_PHONE)
    );
  };

  useEffect(
    () => setShowBasketIcon(showBasket === false && getTotalDevices(merchant) > 0),
    [merchant, showBasket],
  );

  const setShowSubmitModal = (isShow) => {
    setShowSubmitModalWithoutTagging(isShow);
    if (paymentRequired) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.reserveFundsPageLoad(
        merchant?.account?.business_type,
        customerType,
        assistantId,
      );
    }
  };

  useEffect(() => setPaymentRequired(hasBuyOnlineDevice), [hasBuyOnlineDevice]);

  const handleQuickNavigate = async (
    stepName: string,
    subStepName: string,
    returnPath?: string,
    doSave?: boolean,
  ) => {
    if (doSave) {
      setIsLoading(true);
      await steps[currentStep].onStepSave();
    }

    const returnStepPath = returnPath !== undefined ? returnPath : steps[currentStep].route;
    setScreenUrlQuickNavigateBack(returnStepPath);
    setIsQuickNavigateToSingleStep(!!subStepName);
    let stepIndex = getStepIndex(stepName, subStepName);
    const nextStep = steps[stepIndex];
    props.history.push(nextStep.route);
  };

  const createRoute = (routePath, RouteComponent, contextProvider, steps, stepIndex) => {
    const Provider = contextProvider ? contextProvider : null;
    if (!steps[stepIndex].hide || !steps[stepIndex].hide()) {
      return (
        <Route
          path={`${routePath}`}
          render={(props) => (
            <>
              {contextProvider /* @ts-ignore */ ? (
                <Provider
                  {...props}
                  toggleNext={(enabled) => setNextEnabled(enabled)}
                  showPageLoader={(show) => setIsLoading(show)}
                >
                  <RouteComponent
                    {...props}
                    toggleNext={(enabled) => setNextEnabled(enabled)}
                    triggerNext={() => handleConfirmedNext()}
                    quickNavigate={(
                      stepName,
                      subStepName,
                      returnPath?: string,
                      doSave?: boolean,
                    ) => {
                      handleQuickNavigate(stepName, subStepName, returnPath, doSave);
                    }}
                    showPageLoader={(show) => setIsLoading(show)}
                    isQuickNavigateState={!!screenUrlQuickNavigateBack}
                    unsetQuickNavigate={unsetQuickNavigate}
                  />
                </Provider>
              ) : (
                <RouteComponent
                  {...props}
                  toggleNext={(enabled) => setNextEnabled(enabled)}
                  triggerNext={() => handleConfirmedNext()}
                  quickNavigate={(stepName, subStepName, returnPath?: string, doSave?: boolean) =>
                    handleQuickNavigate(stepName, subStepName, returnPath, doSave)
                  }
                  isQuickNavigateState={!!screenUrlQuickNavigateBack}
                  unsetQuickNavigate={unsetQuickNavigate}
                />
              )}
            </>
          )}
          key={routePath}
        />
      );
    } else {
      const firstNonHiddenStepIndex = steps.findIndex(
        (step, index) => index >= stepIndex && !(step.hide && step.hide()),
      );
      return (
        <Route key={routePath} path={routePath}>
          <Redirect to={steps[firstNonHiddenStepIndex].route} />
        </Route>
      );
    }
  };

  const routes = unfilteredSteps.map((step, index) =>
    createRoute(step.route, step.component, step.contextProvider, unfilteredSteps, index),
  );
  const currentStep = steps.findIndex((step) => matchPath(props.location.pathname, step.route));

  const handleBackPrivate = async () => {
    setNextEnabled(false);
    await steps[currentStep].onStepSave(true);

    if (currentStep > 0) {
      const lastStep = steps[currentStep - 1];
      props.history.push(lastStep.route);
    } else if (steps[0].backRoute) {
      props.history.push(steps[0].backRoute);
    }
  };

  const handleNextPrivate = async () => {
    setNextEnabled(false);

    if (isOnlyTapOnPhoneSelected()) {
      setShowTapOnPhoneModal(true);
    } else if (steps[currentStep].nextNeedsConfirmationMessage) {
      setShowSubmitModal(true);
    } else if (!!steps[currentStep].nextNeedsSovIncompleteModal && isIncompleteSOVJourney) {
      setShowSovInfoModal(true);
    } else {
      handleConfirmedNext();
    }
  };

  const isNextStepASubStep = () => {
    if (currentStep < steps.length - 1) {
      return steps[currentStep].stepName === steps[currentStep + 1].stepName;
    } else {
      return false;
    }
  };

  const isPreviousStepASubStep = () => {
    if (currentStep > 0) {
      return steps[currentStep].stepName === steps[currentStep - 1].stepName;
    } else {
      return false;
    }
  };

  const unsetQuickNavigate = () => {
    setScreenUrlQuickNavigateBack(null);
    setIsQuickNavigateToSingleStep(null);
  };

  const hasAllDetailsFilled = () => {
    // Only checking for 1 case ATM
    const hasGateway = isSpecificDeviceSelected(
      DEVICE_NAMES.VODAPAY_ECOM_GATEWAY,
      merchant?.opportunity?.deliveries,
    );

    if (hasGateway) {
      return merchant.account.business_website !== '';
    }

    return true;
  };

  const handleConfirmedNext = async () => {
    setIsLoading(true);
    setShowSovInfoModal(false);
    await steps[currentStep].onStepSave();

    if (steps[currentStep].onStepComplete) {
      steps[currentStep].onStepComplete();
    }
    if (
      screenUrlQuickNavigateBack &&
      (isQuickNavigateToSingleStep || !isNextStepASubStep()) &&
      !isIncompleteSOVJourney &&
      hasAllDetailsFilled()
    ) {
      props.history.push(screenUrlQuickNavigateBack);
      setScreenUrlQuickNavigateBack(null);
    } else if (currentStep < steps.length - 1) {
      const nextStep = steps[currentStep + 1];
      props.history.push(nextStep.route);
    } else if (steps[steps.length - 1].nextRoute) {
      props.history.push(steps[steps.length - 1].nextRoute);
    }
    setIsLoading(false);
  };

  const onHideSubmitModal = () => {
    setShowSubmitModal(false);
    setNextEnabled(true);
  };

  const onSubmitConfirmed = () => {
    handleConfirmedNext();
    setNextEnabled(false);
    setShowSubmitModal(false);
  };

  const onReserveFunds = async () => {
    setNextEnabled(false);
    setShowSubmitModal(false);
    setIsLoading(true); // remains true until the redirect
    await steps[currentStep].onStepSave();
    try {
      await reserveFunds();
    } catch (error) {
      // We might want to redirect to a failure screen here
      setNextEnabled(true);
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    await steps[currentStep].onStepSave();
  };

  const getNextButtonLabel = () =>
    screenUrlQuickNavigateBack &&
    (isQuickNavigateToSingleStep || !isNextStepASubStep()) &&
    !isIncompleteSOVJourney
      ? 'Update'
      : steps[currentStep].nextButtonLabel;

  const handleCancel = () => (window.location.href = publicSiteUrl);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const BrowserNavigationButtonHandler = () => (
    <Prompt
      when={true}
      message={(newUrl) => {
        // if no back route defined for current step and if we are on the first step
        // and click the browser back button, and if the last page was the screen
        // before the first step, or if the step 'disableBack' parameter is set to true
        // then ignore back button click
        if (
          (!screenUrlQuickNavigateBack &&
            currentStep === 0 &&
            !steps[currentStep].backRoute &&
            newUrl.pathname !== steps[currentStep + 1].route) ||
          (currentStep > 0 &&
            newUrl.pathname === steps[currentStep - 1].route &&
            steps[currentStep].disableBack)
        ) {
          return false;
        }

        return true;
      }}
    />
  );

  const getBasketMiniMode = () =>
    steps[currentStep].stepName !== Steps.CHOOSE_A_SOLUTION ||
    steps[currentStep].subStepName === Steps.CHOOSE_ECOMMERCE_PRODUCT;

  const MainPageContent = () => {
    return (
      <div className="CreateAccount is-fullheight">
        {currentStep >= 0 && (
          <ProgressStepsBar
            steps={steps}
            currentStep={currentStep}
            automationIdPrefix={props.automationIdPrefix}
            onBack={handleNavigateBack}
            hideBack={
              (screenUrlQuickNavigateBack && !isPreviousStepASubStep()) ||
              (currentStep === 0 && !steps[currentStep].backRoute) ||
              steps[currentStep].disableBack
            }
          />
        )}

        <div className="step">
          <Switch>{routes}</Switch>
        </div>

        {currentStep >= 0 && (
          <>
            <FormFooter />

            {!steps[currentStep].hideNavBar && (
              <NavigationFooter
                onBack={handleNavigateBack}
                onNext={handleNavigateNext}
                onCancel={handleCancel} /* @ts-ignore */
                onSave={handleSave}
                showBasketIcon={showBasketIcon}
                showBasket={showBasket}
                setShowBasket={setShowBasket}
                isBasketMiniMode={getBasketMiniMode}
                nextEnabled={nextEnabled}
                hideNext={steps[currentStep].disableNext}
                hideBack={
                  (screenUrlQuickNavigateBack &&
                    (isQuickNavigateToSingleStep || !isPreviousStepASubStep())) ||
                  (currentStep === 0 && !steps[currentStep].backRoute) ||
                  steps[currentStep].disableBack
                }
                hideCancel={true}
                nextButtonLabel={getNextButtonLabel()}
                customerType={customerType}
              />
            )}

            {!steps[currentStep].hideHelpFab && (
              <HelpFloatingActionButtonOnboarding customerType={salesforceContext.customerType} />
            )}

            <BrowserNavigationButtonHandler />
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    // TODO: move into state
    const reducer = {
      apply: (message: NavigateToNextStepMessage | NavigateToPreviousStepMessage) => {
        switch (message.name) {
          case WizardMessageNames.NAVIGATE_TO_NEXT_STEP:
            return {
              effect: () => handleNextPrivate(),
            };

          case WizardMessageNames.NAVIGATE_TO_PREVIOUS_STEP:
            return {
              effect: () => handleBackPrivate(),
            };
        }

        return {};
      },
      applicableMessageNames: [
        WizardMessageNames.NAVIGATE_TO_NEXT_STEP,
        WizardMessageNames.NAVIGATE_TO_PREVIOUS_STEP,
      ],
    };

    const unregisterReducer = registerReducer(reducer);
    return () => unregisterReducer();
  }, [salesforceContext, isQuickNavigateToSingleStep, screenUrlQuickNavigateBack]);

  useEffect(() => {
    const unregisterReducer = registerReducer(wizardReducer);
    return () => unregisterReducer();
  }, []);

  return (
    <div className="Wizard full-height">
      <SubmitApplicationModal
        show={showSubmitModal}
        onCancelButtonClick={onHideSubmitModal}
        onConfirmButtonClick={paymentRequired ? onReserveFunds : onSubmitConfirmed}
        paymentGatewayAmount={paymentGatewayAmount}
        paymentRequired={paymentRequired}
      />
      <SovModal handleConfirm={() => onSubmitConfirmed()} show={showSovInfoModal} />
      {showTapOnPhoneModal && <TapOnPhoneModal onClose={() => setShowTapOnPhoneModal(false)} />}
      <FullPageLoader showLoader={!wizard || wizard.pageLoaderEnabled} />
      {isLoadingFromSessionStorage ? (
        <FullPageLoader showLoader={isLoadingFromSessionStorage} />
      ) : (
        MainPageContent()
      )}
    </div>
  );
}

export default withRouter(Wizard);
